// src/components/CheckPrint.js — print checks on pre-printed check paper
// [marker: bills v1]
// Layouts match QuickBooks' check paper, so the same stock works:
//   voucher  — one check per page, check on top, two stubs below (8.5" × 11")
//   standard — three checks per page, no stubs
// Only the variable parts print (date, payee, amount, words, address, memo, stubs); the bank, the
// routing/account numbers and the check number are already on the paper. Offsets nudge everything
// if the printer feeds a little off.
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
  'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
function under1000(n) {
  const h = Math.floor(n / 100); const r = n % 100;
  const parts = [];
  if (h) parts.push(`${ONES[h]} hundred`);
  if (r) parts.push(r < 20 ? ONES[r] : TENS[Math.floor(r / 10)] + (r % 10 ? '-' + ONES[r % 10] : ''));
  return parts.join(' ');
}
// 650 → "Six hundred fifty and 00/100"
export function amountInWords(amount) {
  const cents = Math.round(Math.abs(parseFloat(amount) || 0) * 100);
  let dollars = Math.floor(cents / 100); const c = cents % 100;
  if (dollars === 0) return `Zero and ${String(c).padStart(2, '0')}/100`;
  const scales = ['', ' thousand', ' million', ' billion'];
  const parts = []; let i = 0;
  while (dollars > 0) {
    const chunk = dollars % 1000;
    if (chunk) parts.unshift(under1000(chunk) + scales[i]);
    dollars = Math.floor(dollars / 1000); i++;
  }
  const w = parts.join(' ');
  return `${w.charAt(0).toUpperCase()}${w.slice(1)} and ${String(c).padStart(2, '0')}/100`;
}

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
const money = (n) => (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mdY = (s) => { if (!s) return ''; const [y, m, d] = String(s).slice(0, 10).split('-'); return `${m}/${d}/${y}`; };

// the check face (3.5" tall), positioned from its own top-left corner
function checkFace(c, st, outline) {
  const pos = (top, left, extra = '') => `position:absolute;top:${top}in;left:${left}in;${extra}`;
  const words = `${amountInWords(c.amount)}`;
  const stars = '*'.repeat(Math.max(4, 90 - words.length));
  let h = `<div style="position:absolute;inset:0;font-family:'Courier New',Courier,monospace;font-size:${st.fontSize}pt;color:#000">`;
  if (outline) h += `<div style="position:absolute;inset:0.05in;border:1px dashed #bbb"></div><div style="${pos(0.15, 6.1, 'font:9pt Arial;color:#999')}">TEST — hold behind a check</div>`;
  if (st.printCompany) {
    h += `<div style="${pos(0.3, 0.35, 'font-family:Arial;font-size:9.5pt;line-height:1.25;max-width:3.6in')}"><strong>${esc(st.orgName)}</strong><br>${esc(st.orgAddress)}${st.orgPhone ? '<br>' + esc(st.orgPhone) : ''}</div>`;
  }
  h += `<div style="${pos(0.69, 6.8)}">${esc(mdY(c.date))}</div>`;
  h += `<div style="${pos(1.31, 1.0, 'width:5.4in;white-space:nowrap;overflow:hidden')}">${esc(c.payee)}</div>`;
  h += `<div style="${pos(1.31, 6.85)}">**${esc(money(c.amount))}</div>`;
  h += `<div style="${pos(1.69, 0.35, 'width:7.1in;white-space:nowrap;overflow:hidden')}">${esc(words)}${stars}</div>`;
  h += `<div style="${pos(2.06, 1.0, 'width:3.6in;line-height:1.2;white-space:pre-line')}">${esc(c.payee)}${c.address ? '\n' + esc(c.address) : ''}</div>`;
  if (c.memo) h += `<div style="${pos(2.93, 0.6, 'width:3.4in;white-space:nowrap;overflow:hidden')}">${esc(c.memo)}</div>`;
  return h + '</div>';
}

// a voucher stub (3.5" tall)
function stubHtml(c, outline) {
  let h = `<div style="position:absolute;inset:0;padding:0.3in 0.4in;font-family:Arial,sans-serif;font-size:9pt;color:#000">`;
  if (outline) h += '<div style="position:absolute;inset:0.05in;border:1px dashed #ddd"></div>';
  h += `<div style="display:flex;justify-content:space-between;font-weight:700;margin-bottom:6px"><span>${esc(c.payee)}</span><span>${esc(mdY(c.date))}${c.number ? '&nbsp;&nbsp;&nbsp;Check ' + esc(c.number) : ''}</span></div>`;
  h += '<table style="width:100%;border-collapse:collapse;font-size:8.5pt">';
  if (c.stub?.kind === 'bills') {
    // [marker: books-pro v1] vendor credits used show in their own column
    h += '<tr style="border-bottom:1px solid #000"><th align="left">Date</th><th align="left">Type</th><th align="left">Reference</th><th align="right">Original Amt.</th><th align="right">Balance Due</th><th align="right">Credits</th><th align="right">Payment</th></tr>';
    (c.stub.rows || []).slice(0, 9).forEach(r => {
      const credit = parseFloat(r.credit) || 0;
      h += `<tr><td>${esc(mdY(r.date))}</td><td>Bill</td><td>${esc(r.ref || '')}</td><td align="right">${money(r.original)}</td><td align="right">${money(parseFloat(r.balance) + parseFloat(r.paid) + credit)}</td><td align="right">${credit ? money(credit) : ''}</td><td align="right">${money(r.paid)}</td></tr>`;
    });
  } else {
    h += '<tr style="border-bottom:1px solid #000"><th align="left">Account</th><th align="left">Memo</th><th align="right">Amount</th></tr>';
    (c.stub?.rows || []).slice(0, 9).forEach(r => { h += `<tr><td>${esc(r.account)}</td><td>${esc(r.memo || '')}</td><td align="right">${money(r.amount)}</td></tr>`; });
  }
  h += `</table><div style="position:absolute;left:0.4in;right:0.4in;bottom:0.35in;display:flex;justify-content:space-between;border-top:1px solid #000;padding-top:3px">`;
  h += `<span>${esc(c.account_name || '')}</span><strong>${money(c.amount)}</strong></div>`;
  return h + '</div>';
}

export function checksHtml(checks, st, outline = false) {
  const ox = parseFloat(st.offsetX) || 0; const oy = parseFloat(st.offsetY) || 0;
  const page = (inner) => `<div class="njc-page" style="position:relative;width:8.5in;height:11in;overflow:hidden;page-break-after:always;break-after:page"><div style="position:absolute;left:${ox}in;top:${oy}in;width:8.5in;height:11in">${inner}</div></div>`;
  let out = '';
  if (st.style === 'standard') {
    for (let i = 0; i < checks.length; i += 3) {
      out += page(checks.slice(i, i + 3).map((c, k) => `<div style="position:absolute;left:0;top:${k * 3.5}in;width:8.5in;height:3.5in">${checkFace(c, st, outline)}</div>`).join(''));
    }
  } else {
    checks.forEach(c => {
      out += page(`<div style="position:absolute;left:0;top:0;width:8.5in;height:3.5in">${checkFace(c, st, outline)}</div>`
        + `<div style="position:absolute;left:0;top:3.5in;width:8.5in;height:3.5in">${stubHtml(c, outline)}</div>`
        + `<div style="position:absolute;left:0;top:7in;width:8.5in;height:3.5in">${stubHtml(c, outline)}</div>`);
    });
  }
  return out;
}

// Opens the print window. Resolves after it closes.
export function printChecks(checks, st, { outline = false } = {}) {
  return new Promise((resolve) => {
    ['njc-print-root', 'njc-print-style'].forEach(id => { const el = document.getElementById(id); if (el) el.remove(); });
    const root = document.createElement('div'); root.id = 'njc-print-root'; root.innerHTML = checksHtml(checks, st, outline);
    const css = document.createElement('style'); css.id = 'njc-print-style';
    css.textContent = `#njc-print-root{display:none}
@media print{@page{size:8.5in 11in;margin:0}html,body{margin:0!important;padding:0!important;background:#fff!important}
body>*:not(#njc-print-root){display:none!important}#njc-print-root{display:block!important}
#njc-print-root .njc-page:last-child{page-break-after:auto;break-after:auto}}`;
    document.head.appendChild(css); document.body.appendChild(root);
    let done = false;
    const cleanup = () => { if (done) return; done = true; setTimeout(() => { root.remove(); css.remove(); resolve(); }, 400); };
    setTimeout(() => {
      window.addEventListener('afterprint', cleanup, { once: true });
      window.print();
      setTimeout(cleanup, 60000);
    }, 50);
  });
}

export function useCheckSettings() {
  const [st, setSt] = useState(null);
  const reload = () => api.get('/ap/check-settings').then(r => setSt(r.data)).catch(() => setSt({ style: 'voucher', offsetX: 0, offsetY: 0, fontSize: 11 }));
  useEffect(() => { reload(); }, []);
  return [st, setSt, reload];
}

const SAMPLE = {
  date: new Date().toISOString().slice(0, 10), number: '', payee: 'Sample Vendor LLC', address: '123 Main Street\nCranford, NJ 07016',
  amount: 1234.56, memo: 'Acct # 0000', account_name: 'Checking',
  stub: { kind: 'bills', rows: [{ date: new Date().toISOString().slice(0, 10), ref: 'INV-1001', original: 1234.56, paid: 1234.56, balance: 0 }] },
};

// Settings: check style, alignment, company name. canEdit saves for everyone.
export function CheckSettingsModal({ canEdit, onClose, onSaved }) {
  const [st, setSt] = useCheckSettings();
  const [saving, setSaving] = useState(false);
  if (!st) return null;
  const set = (k, v) => setSt(s => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const r = await api.put('/ap/check-settings', st); toast.success('Check settings saved.'); onSaved && onSaved({ ...st, ...r.data }); onClose(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };
  const nudge = (k, d) => set(k, Math.round(((parseFloat(st[k]) || 0) + d) * 100) / 100);
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1680 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header"><span className="modal-title">Check Printing Setup</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Check paper</label>
            <select className="form-select" value={st.style} disabled={!canEdit} onChange={e => set('style', e.target.value)}>
              <option value="voucher">Voucher — check on top, 2 stubs below (1 per page)</option>
              <option value="standard">Standard — 3 checks per page</option>
            </select>
            <p className="form-hint">The same QuickBooks-compatible check paper you use now works here.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['offsetY', 'Move up / down', '↑', '↓'], ['offsetX', 'Move left / right', '←', '→']].map(([k, label, a, b]) => (
              <div key={k} className="form-group" style={{ margin: 0 }}>
                <label className="form-label">{label} (inches)</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={!canEdit} onClick={() => nudge(k, -0.05)}>{a}</button>
                  <input className="form-input" type="number" step="0.01" value={st[k]} disabled={!canEdit} onChange={e => set(k, e.target.value)} style={{ textAlign: 'center' }} />
                  <button type="button" className="btn btn-secondary btn-sm" disabled={!canEdit} onClick={() => nudge(k, 0.05)}>{b}</button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Text size (pt)</label>
              <input className="form-input" type="number" min="8" max="14" value={st.fontSize} disabled={!canEdit} onChange={e => set('fontSize', e.target.value)} />
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.875rem', paddingBottom: 8 }}>
              <input type="checkbox" checked={!!st.printCompany} disabled={!canEdit} onChange={e => set('printCompany', e.target.checked)} />
              Print our name &amp; address (only if the paper doesn’t have it)
            </label>
          </div>
          <div style={{ background: 'var(--gray-50)', borderRadius: 6, padding: '10px 12px', fontSize: '0.82rem', lineHeight: 1.5 }}>
            <strong>Lining it up:</strong> print a test on plain paper, hold it behind a blank check against a window, and nudge until
            the date, name and amount sit on the lines. In the print window choose <em>Actual size / 100%</em> (not “Fit to page”).
          </div>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-secondary" onClick={() => printChecks([SAMPLE], st, { outline: true })}>🖨 Print a test on plain paper</button>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
            {canEdit && <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
          </span>
        </div>
      </div>
    </div>
  );
}

// Print Checks: the checks waiting to be printed for one bank account.
// preselect: [{source, id}] to tick only those (e.g. right after Pay Bills).
export function PrintChecksModal({ canEdit, preselect, onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [checks, setChecks] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [first, setFirst] = useState('');
  const [st, setSt] = useCheckSettings();
  const [setup, setSetup] = useState(false);
  const [busy, setBusy] = useState(false);
  const key = (c) => `${c.source}:${c.id}`;

  useEffect(() => {
    api.get('/ap/checks/queue').then(r => {
      const all = r.data || [];
      const ids = Array.from(new Set(all.map(c => c.account_id)));
      api.get('/bookkeeping/register-accounts').then(a => {
        const banks = (a.data || []).filter(x => x.account_type === 'bank');
        setAccounts(banks.map(b => ({ ...b, waiting: all.filter(c => c.account_id === b.id).length })));
        const want = preselect && preselect.length ? all.find(c => preselect.some(p => p.source === c.source && p.id === c.id))?.account_id : null;
        setAccountId(want || ids[0] || banks[0]?.id || null);
      }).catch(() => {});
    }).catch(() => setChecks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!accountId) return;
    setChecks(null);
    api.get(`/ap/checks/queue?accountId=${accountId}`).then(r => {
      const list = r.data || [];
      setChecks(list);
      setPicked(new Set(list.filter(c => !preselect || !preselect.length || preselect.some(p => p.source === c.source && p.id === c.id)).map(key)));
    }).catch(() => setChecks([]));
    api.get(`/ap/checks/next-number?accountId=${accountId}`).then(r => setFirst(r.data.next || '')).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const chosen = (checks || []).filter(c => picked.has(key(c)));
  const total = chosen.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const numbered = () => {
    let n = first.trim();
    return chosen.map(c => {
      const out = { ...c, number: n };
      if (/^\d+$/.test(n)) n = String(parseInt(n, 10) + 1).padStart(n.length, '0');
      return out;
    });
  };
  const print = async () => {
    if (!chosen.length) return toast.error('Tick the checks to print.');
    if (!/^\d+$/.test(first.trim())) return toast.error('Enter the number printed on the first check in the printer.');
    const list = numbered();
    setBusy(true);
    try {
      await printChecks(list, st);
      const ok = window.confirm(`Did all ${list.length} check${list.length === 1 ? '' : 's'} print correctly?\n\nOK = yes, record them as checks ${list[0].number}${list.length > 1 ? '–' + list[list.length - 1].number : ''}.\nCancel = no, keep them waiting so you can print again.`);
      if (!ok) { toast('Nothing changed — the checks are still waiting to be printed.', { icon: 'ℹ️' }); return; }
      await api.post('/ap/checks/printed', { items: list.map(c => ({ source: c.source, id: c.id, number: c.number })) });
      toast.success(`Recorded check${list.length === 1 ? '' : 's'} ${list[0].number}${list.length > 1 ? '–' + list[list.length - 1].number : ''}.`);
      onDone && onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not record the checks.'); }
    finally { setBusy(false); }
  };

  return (
    <>
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1570 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div className="modal-header"><span className="modal-title">Print Checks</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Bank account</label>
              <select className="form-select" value={accountId || ''} onChange={e => setAccountId(parseInt(e.target.value, 10))}>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.account_last_four ? ` ••${a.account_last_four}` : ''} — {a.waiting} waiting</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">First check number</label>
              <input className="form-input" value={first} onChange={e => setFirst(e.target.value.replace(/[^\d]/g, ''))} placeholder="On the paper" />
            </div>
          </div>
          {checks === null ? <div className="spinner" /> : checks.length === 0 ? (
            <div className="empty-state" style={{ padding: 24 }}><div className="empty-state-icon">✅</div><h3>No checks waiting to be printed</h3>
              <p>Choose “To be printed” when you pay bills or write a check, and it shows up here.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th style={{ width: 30 }}><input type="checkbox" checked={chosen.length === checks.length} onChange={e => setPicked(e.target.checked ? new Set(checks.map(key)) : new Set())} /></th>
                  <th>Date</th><th>Pay to</th><th>Memo</th><th style={{ textAlign: 'right' }}>Amount</th>
                </tr></thead>
                <tbody>
                  {checks.map(c => (
                    <tr key={key(c)} style={{ cursor: 'pointer' }} onClick={() => setPicked(p => { const n = new Set(p); n.has(key(c)) ? n.delete(key(c)) : n.add(key(c)); return n; })}>
                      <td><input type="checkbox" readOnly checked={picked.has(key(c))} /></td>
                      <td>{mdY(c.date)}</td>
                      <td><strong>{c.payee}</strong>{!c.address && <div style={{ fontSize: '0.72rem', color: '#b45309' }}>No address — add one to the vendor if you mail it</div>}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>{c.memo}</td>
                      <td style={{ textAlign: 'right' }}>${money(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {checks && checks.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--gray-600)' }}>{st ? (st.style === 'standard' ? 'Standard checks (3 per page)' : 'Voucher checks (1 per page with 2 stubs)') : ''} ·{' '}
                <button className="btn btn-ghost btn-sm" style={{ padding: 0 }} onClick={() => setSetup(true)}>Paper &amp; alignment…</button></span>
              <span>{chosen.length} check{chosen.length === 1 ? '' : 's'} · <strong>${money(total)}</strong></span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={print} disabled={busy || !canEdit || !chosen.length || !st}>{busy ? 'Printing…' : `🖨 Print ${chosen.length || ''} Check${chosen.length === 1 ? '' : 's'}`}</button>
        </div>
      </div>
    </div>
    {setup && <CheckSettingsModal canEdit={canEdit} onClose={() => setSetup(false)} onSaved={v => setSt(s => ({ ...s, ...v }))} />}
    </>
  );
}

// Reprint (or print once) one check that already has its data: used from a bill payment or a register check.
export async function printOneCheck(source, id, numberOverride) {
  const [d, s] = await Promise.all([api.post('/ap/checks/data', { items: [{ source, id }] }), api.get('/ap/check-settings')]);
  const c = (d.data || [])[0];
  if (!c) throw new Error('Check not found.');
  if (numberOverride) c.number = numberOverride;
  await printChecks([c], s.data);
  return c;
}
